import Phaser from "phaser";
import { Palette } from "../ui/palette";
import { makeButton } from "../ui/widgets";

/** Mallorca ending. The founders' celebration on the Mallorca beach plays as a
 *  full-screen video backdrop (public/assets/finale.mp4) with no on-screen text. */
/** Playback speed for the celebration clip. Below 1 slows it into a gentle,
 *  slow-motion sunset backdrop (also stretches how long it plays before the
 *  cross-fade to credits). */
const ENDING_PLAYBACK_RATE = 0.75;

/** Begin the cross-fade to the credits this long (real time) before the clip ends,
 *  so the credits start rolling sooner. */
const FADE_LEAD_MS = 500;

export class EndingScene extends Phaser.Scene {
  private video?: Phaser.GameObjects.Video;
  private leaving = false;

  constructor() {
    super("Ending");
  }

  preload() {
    this.load.video("mallorca-clip", "assets/finale.mp4");
  }

  create() {
    const { width, height } = this.scale;

    // Sunset celebration: the founders' victory clip fills the screen as the backdrop.
    // The clip plays silently (the audio track is stripped from finale.mp4); muting
    // here is belt-and-suspenders and lets the video autoplay reliably (browsers
    // block autoplay-with-sound).
    this.video = this.add.video(width / 2, height / 2, "mallorca-clip").setOrigin(0.5);
    this.video.setMute(true);
    this.video.on("play", () => {
      this.coverFit();
      this.video!.setPlaybackRate(ENDING_PLAYBACK_RATE); // slow-motion celebration
      // Start the cross-fade to credits ~half a second (real time) before the clip
      // ends, so the credits come sooner. Real runtime = duration / playbackRate.
      const dur = this.video!.getDuration();
      if (dur && isFinite(dur)) {
        const leadMs = Math.max(0, (dur / ENDING_PLAYBACK_RATE) * 1000 - FADE_LEAD_MS);
        this.time.delayedCall(leadMs, () => this.toCredits());
      }
    });
    // Play once, then cross-fade to the rolling credits (fade through black).
    this.video.play(false);
    this.video.setPlaybackRate(ENDING_PLAYBACK_RATE);
    this.video.once("complete", () => this.toCredits()); // safety net if duration is unknown
    this.coverFit();

    // No on-screen text on the ending page - just the video and a Skip button.
    makeButton(this, width - 74, height - 28, "Skip  ▶", () => this.toCredits(), {
      width: 120,
      height: 36,
      fontSize: "14px",
      fill: Palette.panel,
    });
  }

  /** Cross-fade (through black) from the celebration video to the rolling credits.
   *  Fires either when the clip finishes or when Skip is pressed - whichever first. */
  private toCredits() {
    if (this.leaving) return;
    this.leaving = true;
    this.cameras.main.fadeOut(700, 0, 0, 0);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () =>
      this.scene.start("Credits"),
    );
  }

  /** Scale the video so it covers the canvas (no letterboxing), centered. */
  private coverFit() {
    const { width, height } = this.scale;
    const vw = this.video!.width || 1264;
    const vh = this.video!.height || 720;
    const scale = Math.max(width / vw, height / vh);
    this.video!.setDisplaySize(vw * scale, vh * scale);
  }
}
